import { ref } from 'vue';
import { useApi } from './useApi';
import router from '@/router';

// Lock state keys
const LOCK_KEY = 'screen_locked';
const LOCK_PATH_KEY = 'locked_from_path';

// Idle timeout in milliseconds (5 minutes)
const IDLE_TIMEOUT = 20 * 60 * 1000;

// Shared reactive state
const isLocked = ref(sessionStorage.getItem(LOCK_KEY) === 'true');

let idleTimer = null;
let eventsBound = false;

/**
 * Composable for lock screen functionality
 * Provides idle detection, lock state management, and password verification
 */
export function useLockScreen() {
    const { api } = useApi();

    const unlockError = ref(null);
    const unlockLoading = ref(false);
    const hasPin = ref(false);

    // Events that reset the idle timer
    const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    /**
     * Whether the idle auto-lock is enabled for the current user.
     * Per-user preference stored on the user record; only an explicit `false`
     * disables it, so older sessions without the field keep the auto-lock.
     */
    const isLockEnabled = () => {
        try {
            const stored = sessionStorage.getItem('authUser');
            if (stored) {
                return JSON.parse(stored).lock_screen_enabled !== false;
            }
        } catch (e) { /* ignore parse error */ }
        return true;
    };

    /**
     * Reset the idle timer on user activity
     */
    const resetIdleTimer = () => {
        if (isLocked.value) return; // Don't reset if already locked

        if (idleTimer) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }

        // Respect the per-user toggle: don't arm the timer when disabled
        if (!isLockEnabled()) return;

        idleTimer = setTimeout(() => {
            lockScreen();
        }, IDLE_TIMEOUT);
    };

    /**
     * Lock the screen and redirect to lock screen page
     */
    const lockScreen = () => {
        // Respect the per-user toggle
        if (!isLockEnabled()) return;

        // Don't lock if on login or lock-screen page already
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/lock-screen') return;

        // Don't lock if no auth token
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        isLocked.value = true;
        sessionStorage.setItem(LOCK_KEY, 'true');
        sessionStorage.setItem(LOCK_PATH_KEY, currentPath);

        router.push('/lock-screen');
    };

    /**
     * Fetch PIN status from API
     */
    const fetchPinStatus = async () => {
        try {
            const response = await api.get('/api/pin/status');
            hasPin.value = response.data.has_pin;
        } catch (err) {
            hasPin.value = false;
        }
    };

    /**
     * Unlock the screen by verifying password
     * @param {string} password - User's password
     * @returns {Promise<boolean>} Whether unlock was successful
     */
    const unlockScreen = async (password) => {
        unlockLoading.value = true;
        unlockError.value = null;

        try {
            await api.post('/api/verify-password', { password });

            // Success - unlock
            isLocked.value = false;
            sessionStorage.removeItem(LOCK_KEY);

            const returnPath = sessionStorage.getItem(LOCK_PATH_KEY) || '/';
            sessionStorage.removeItem(LOCK_PATH_KEY);

            // Restart idle timer
            resetIdleTimer();

            // Navigate back
            router.push(returnPath);
            return true;
        } catch (err) {
            unlockError.value = err.response?.data?.message || 'Incorrect password. Please try again.';
            return false;
        } finally {
            unlockLoading.value = false;
        }
    };

    /**
     * Unlock the screen by verifying PIN
     * @param {string} pin - User's 6-digit PIN
     * @returns {Promise<boolean>} Whether unlock was successful
     */
    const unlockWithPin = async (pin) => {
        unlockLoading.value = true;
        unlockError.value = null;

        try {
            await api.post('/api/verify-password', { pin });

            // Success - unlock
            isLocked.value = false;
            sessionStorage.removeItem(LOCK_KEY);

            const returnPath = sessionStorage.getItem(LOCK_PATH_KEY) || '/';
            sessionStorage.removeItem(LOCK_PATH_KEY);

            // Restart idle timer
            resetIdleTimer();

            // Navigate back
            router.push(returnPath);
            return true;
        } catch (err) {
            unlockError.value = err.response?.data?.message || 'Incorrect PIN. Please try again.';
            return false;
        } finally {
            unlockLoading.value = false;
        }
    };

    /**
     * Start monitoring for idle activity
     */
    const startIdleTimer = () => {
        if (!eventsBound) {
            ACTIVITY_EVENTS.forEach(event => {
                document.addEventListener(event, resetIdleTimer, { passive: true });
            });
            eventsBound = true;
        }

        // Always (re)arm the timer so this can be called to apply a freshly
        // enabled lock-screen preference, not just on first bind.
        resetIdleTimer();
    };

    /**
     * Stop monitoring for idle activity
     */
    const stopIdleTimer = () => {
        if (idleTimer) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }

        ACTIVITY_EVENTS.forEach(event => {
            document.removeEventListener(event, resetIdleTimer);
        });

        eventsBound = false;
    };

    /**
     * Get the locked-from path
     */
    const getLockedFromPath = () => {
        return sessionStorage.getItem(LOCK_PATH_KEY) || '/';
    };

    /**
     * Force clear lock state (used on logout)
     */
    const clearLockState = () => {
        isLocked.value = false;
        sessionStorage.removeItem(LOCK_KEY);
        sessionStorage.removeItem(LOCK_PATH_KEY);
        stopIdleTimer();
    };

    return {
        // State
        isLocked,
        unlockError,
        unlockLoading,
        hasPin,

        // Methods
        lockScreen,
        unlockScreen,
        unlockWithPin,
        fetchPinStatus,
        startIdleTimer,
        stopIdleTimer,
        clearLockState,
        getLockedFromPath,
        isLockEnabled,
    };
}

export default useLockScreen;
